import React, { useMemo } from 'react';
import './YearInReading.css';

function YearInReading({ books, year = new Date().getFullYear() }) {
  const yearStats = useMemo(() => {
    const yearBooks = books.filter(book => {
      const bookYear = book.finishedReadingAt 
        ? new Date(book.finishedReadingAt.seconds * 1000).getFullYear()
        : book.createdAt 
        ? new Date(book.createdAt.seconds * 1000).getFullYear()
        : new Date().getFullYear();
      return bookYear === year;
    });

    const finishedBooks = yearBooks.filter(book => 
      (book.readingStatus || book.status) === 'finished'
    );

    const totalPages = finishedBooks.reduce((total, book) => 
      total + (book.pageCount || 0), 0
    );

    const avgPages = finishedBooks.length > 0 
      ? Math.round(totalPages / finishedBooks.length) 
      : 0;

    // Genre breakdown
    const genreCount = {};
    finishedBooks.forEach(book => {
      const genres = book.categories || ['Fiction'];
      genres.forEach(genre => {
        genreCount[genre] = (genreCount[genre] || 0) + 1;
      });
    });

    const topGenres = Object.entries(genreCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);

    // Author diversity
    const authors = new Set();
    finishedBooks.forEach(book => {
      if (book.authors) {
        book.authors.forEach(author => authors.add(author));
      }
    });

    // Monthly breakdown
    const monthlyCount = new Array(12).fill(0);
    finishedBooks.forEach(book => {
      if (book.finishedReadingAt) {
        const month = new Date(book.finishedReadingAt.seconds * 1000).getMonth();
        monthlyCount[month]++;
      }
    });

    const bestMonth = monthlyCount.indexOf(Math.max(...monthlyCount));
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return {
      totalBooks: finishedBooks.length,
      totalPages,
      avgPages,
      topGenres,
      uniqueAuthors: authors.size,
      bestMonth: monthNames[bestMonth],
      monthlyCount,
      longestBook: finishedBooks.reduce((longest, book) => 
        (book.pageCount || 0) > (longest.pageCount || 0) ? book : longest, 
        finishedBooks[0] || {}
      ),
      shortestBook: finishedBooks.reduce((shortest, book) => 
        (book.pageCount || Infinity) < (shortest.pageCount || Infinity) ? book : shortest,
        finishedBooks[0] || {}
      )
    };
  }, [books, year]);

  if (!books.length) {
    return (
      <div className="year-in-reading card">
        <h2>📚 {year} Year in Reading</h2>
        <p>Start reading to see your year-end summary!</p>
      </div>
    );
  }

  return (
    <div className="year-in-reading">
      <div className="year-header">
        <h2>📚 Your {year} Year in Reading</h2>
        <p>A look back at your reading journey</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📖</div>
          <div className="stat-content">
            <span className="stat-number">{yearStats.totalBooks}</span>
            <span className="stat-label">Books Read</span>
          </div>
          <div className="stat-visual">
            <div className="book-stack">
              {Array(Math.min(yearStats.totalBooks, 5)).fill().map((_, i) => (
                <div key={i} className="mini-book" style={{
                  transform: `translateY(${i * -2}px) rotate(${(i - 2) * 2}deg)`
                }} />
              ))}
            </div>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <span className="stat-number">{yearStats.totalPages.toLocaleString()}</span>
            <span className="stat-label">Pages Read</span>
          </div>
          <div className="stat-detail">
            <span>≈ {yearStats.avgPages} per book</span>
          </div>
        </div>

        <div className="stat-card accent">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-number">{yearStats.uniqueAuthors}</span>
            <span className="stat-label">Unique Authors</span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <span className="stat-number">{yearStats.bestMonth}</span>
            <span className="stat-label">Best Reading Month</span>
          </div>
        </div>
      </div>

      <div className="insights-grid">
        <div className="insight-card genres">
          <h3>📚 Top Genres</h3>
          <div className="genre-list">
            {yearStats.topGenres.map(([genre, count], index) => (
              <div key={genre} className="genre-item">
                <div className="genre-bar">
                  <div 
                    className="genre-fill"
                    style={{
                      width: `${(count / yearStats.topGenres[0][1]) * 100}%`,
                      backgroundColor: `var(--color-${['primary', 'secondary', 'accent'][index] || 'primary'})`
                    }}
                  />
                </div>
                <div className="genre-info">
                  <span className="genre-name">{genre}</span>
                  <span className="genre-count">{count} books</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="insight-card monthly">
          <h3>📊 Monthly Progress</h3>
          <div className="monthly-chart">
            {yearStats.monthlyCount.map((count, month) => (
              <div key={month} className="month-bar">
                <div 
                  className="month-fill"
                  style={{
                    height: `${(count / Math.max(...yearStats.monthlyCount, 1)) * 100}%`
                  }}
                  title={`${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month]}: ${count} books`}
                />
                <span className="month-label">
                  {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][month]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {yearStats.longestBook.title && (
          <div className="insight-card book-highlights">
            <h3>📏 Reading Extremes</h3>
            <div className="book-extremes">
              <div className="extreme-item">
                <span className="extreme-label">📚 Longest Read</span>
                <div className="extreme-book">
                  <strong>{yearStats.longestBook.title}</strong>
                  <span>{yearStats.longestBook.pageCount} pages</span>
                </div>
              </div>
              {yearStats.shortestBook.title && (
                <div className="extreme-item">
                  <span className="extreme-label">⚡ Quickest Read</span>
                  <div className="extreme-book">
                    <strong>{yearStats.shortestBook.title}</strong>
                    <span>{yearStats.shortestBook.pageCount} pages</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {yearStats.totalBooks >= 10 && (
        <div className="achievement-banner">
          <div className="achievement-icon">🏆</div>
          <div className="achievement-text">
            <h3>Reading Champion!</h3>
            <p>You've read {yearStats.totalBooks} books this year. That's amazing!</p>
          </div>
          <div className="achievement-sparkles">✨</div>
        </div>
      )}
    </div>
  );
}

export default YearInReading;